import React, { useState } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../AppIcon';
import NotificationBell from '../NotificationBell';
import { useLogoSettings } from '../../hooks/useLogoSettings';

const Header = () => {
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, userProfile, signOut, loading } = useAuth();
  const { logoIcon, logoText } = useLogoSettings();

  const primaryNavItems = [
    { name: 'Dashboard', path: '/collection-dashboard', icon: 'LayoutDashboard' },
    { name: 'Add Collection', path: '/add-collection', icon: 'Plus' },
    { name: 'Market', path: '/market-analysis', icon: 'TrendingUp' },
    { name: 'News', path: '/news-feed', icon: 'Newspaper' },
  ];

  const isActivePath = (path) => location?.pathname === path;

  const handleSignOut = async () => {
    try {
      await signOut();
      setIsProfileMenuOpen(false);
      setIsMobileMenuOpen(false);
      navigate('/login');
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  const handleProfileMenuClick = (path) => {
    navigate(path);
    setIsProfileMenuOpen(false);
    setIsMobileMenuOpen(false);
  };

  const handleNavigation = (path) => {
    navigate(path);
    setIsMobileMenuOpen(false);
  };

  if (loading) {
    return (
      <header className="fixed top-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-md border-b border-border">
        <div className="w-full px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <div className="w-10 h-10 bg-muted rounded-full animate-pulse"></div>
            </div>
            <div className="hidden lg:flex items-center space-x-4">
              <div className="w-8 h-8 bg-muted rounded-full animate-pulse"></div>
            </div>
          </div>
        </div>
      </header>
    );
  }

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-md border-b border-border">
      <div className="w-full px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <div className="flex items-center">
            <Link to="/homepage" className="flex items-center space-x-2 sm:space-x-3 group">
              <img 
                src={logoIcon}
                alt="Crazy Rare Club Symbol" 
                className="w-9 h-9 sm:w-10 sm:h-10 object-contain transition-transform duration-300 group-hover:scale-105"
                loading="eager"
                fetchpriority="high"
                decoding="async"
              />
              <div className="hidden sm:block">
                <img 
                  src={logoText}
                  alt="Crazy Rare Club" 
                  className="h-7 sm:h-8 object-contain transition-transform duration-300 group-hover:scale-105"
                  loading="eager"
                  fetchpriority="high"
                  decoding="async"
                />
              </div>
            </Link>
          </div>

          {/* Desktop Navigation */}
          {user && (
            <nav className="hidden lg:flex items-center space-x-1">
              {primaryNavItems?.map((item) => (
                <Link
                  key={item?.path}
                  to={item?.path}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 premium-hover ${
                    isActivePath(item?.path)
                      ? 'bg-primary/10 text-primary border border-primary/20' :'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                  }`}
                >
                  <Icon name={item?.icon} size={18} />
                  <span>{item?.name}</span>
                </Link>
              ))}
            </nav>
          )}

          {/* Desktop Actions */}
          <div className="hidden lg:flex items-center space-x-4">
            {user ? (
              <>
                {/* Notifications */}
                <NotificationBell />

                {/* Profile Dropdown */}
                <div className="relative">
                  <button
                    onClick={() => setIsProfileMenuOpen(!isProfileMenuOpen)}
                    className="flex items-center space-x-3 p-1.5 rounded-lg hover:bg-muted/50 transition-colors duration-200 premium-hover min-h-[44px]"
                  >
                    {userProfile?.avatar_url ? (
                      <img 
                        src={userProfile?.avatar_url} 
                        alt="Profile" 
                        className="w-8 h-8 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-8 h-8 bg-muted rounded-full flex items-center justify-center">
                        <Icon name="User" size={16} />
                      </div>
                    )}
                    <div className="hidden md:flex items-center space-x-1">
                      <span className="text-sm font-medium text-foreground">
                        {userProfile?.full_name?.split(' ')?.[0] || 'User'}
                      </span>
                      <Icon name="ChevronDown" size={12} />
                    </div>
                  </button>

                  {/* Profile Dropdown Menu */}
                  {isProfileMenuOpen && (
                    <div className="absolute right-0 mt-2 w-56 bg-popover border border-border rounded-lg shadow-premium-lg animate-scale-in z-50">
                      <div className="py-2">
                        <div className="px-4 py-2 border-b border-border">
                          <p className="text-sm font-medium text-foreground">
                            {userProfile?.full_name || 'User'}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {userProfile?.email}
                          </p>
                          {userProfile?.role === 'admin' && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-primary/10 text-primary mt-1">
                              Administrator
                            </span>
                          )}
                        </div>
                        
                        <button
                          onClick={() => handleProfileMenuClick('/profile-management')}
                          className="flex items-center space-x-3 px-4 py-3 text-sm text-popover-foreground hover:bg-muted/50 transition-colors duration-200 w-full text-left min-h-[44px]"
                        >
                          <Icon name="User" size={16} />
                          <span>Profile Settings</span>
                        </button>

                        {userProfile?.role === 'admin' && (
                          <>
                            <hr className="my-2 border-border" />
                            <button
                              onClick={() => handleProfileMenuClick('/admin-portal')}
                              className="flex items-center space-x-3 px-4 py-3 text-sm text-popover-foreground hover:bg-muted/50 transition-colors duration-200 w-full text-left min-h-[44px]"
                            >
                              <Icon name="Shield" size={16} />
                              <span>Admin Portal</span>
                            </button>
                          </>
                        )}

                        <hr className="my-2 border-border" />
                        
                        <button
                          onClick={() => handleProfileMenuClick('/help-support')}
                          className="flex items-center space-x-3 px-4 py-3 text-sm text-popover-foreground hover:bg-muted/50 transition-colors duration-200 w-full text-left min-h-[44px]"
                        >
                          <Icon name="HelpCircle" size={16} />
                          <span>Help & Support</span>
                        </button>
                        
                        <button
                          onClick={handleSignOut}
                          className="flex items-center space-x-3 px-4 py-3 text-sm text-destructive hover:bg-destructive/10 transition-colors duration-200 w-full text-left min-h-[44px]"
                        >
                          <Icon name="LogOut" size={16} />
                          <span>Sign Out</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  className="px-4 py-2.5 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors duration-200 min-h-[44px] flex items-center"
                >
                  Sign In
                </Link>
                <Link
                  to="/registration"
                  className="px-4 py-2.5 text-sm font-medium bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors duration-200 min-h-[44px] flex items-center"
                >
                  Get Started
                </Link>
              </>
            )}
          </div>

          {/* Mobile Actions + Menu Button */}
          <div className="lg:hidden flex items-center space-x-1">
            {user && (
              <NotificationBell />
            )}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="p-2.5 text-muted-foreground hover:text-foreground transition-colors duration-200 premium-hover rounded-lg min-w-[44px] min-h-[44px] flex items-center justify-center"
              aria-label="Toggle menu"
            >
              <Icon name={isMobileMenuOpen ? "X" : "Menu"} size={22} />
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div className="lg:hidden border-t border-border animate-slide-up">
            <div className="py-3 space-y-1">
              {user ? (
                <>
                  {/* User Info */}
                  <div className="px-4 py-3 border-b border-border mb-2">
                    <div className="flex items-center space-x-3">
                      {userProfile?.avatar_url ? (
                        <img 
                          src={userProfile?.avatar_url} 
                          alt="Profile" 
                          className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                        />
                      ) : (
                        <div className="w-10 h-10 bg-muted rounded-full flex items-center justify-center flex-shrink-0">
                          <Icon name="User" size={18} />
                        </div>
                      )}
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">{userProfile?.full_name || 'User'}</p>
                        <p className="text-xs text-muted-foreground truncate">{userProfile?.email}</p>
                      </div>
                    </div>
                  </div>

                  {/* Primary Nav Items */}
                  {primaryNavItems?.map((item) => (
                    <button
                      key={item?.path}
                      onClick={() => handleNavigation(item?.path)}
                      className={`flex items-center space-x-3 w-full px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 min-h-[48px] ${
                        isActivePath(item?.path)
                          ? 'bg-primary/10 text-primary' :'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                      }`}
                    >
                      <Icon name={item?.icon} size={20} />
                      <span>{item?.name}</span>
                    </button>
                  ))}

                  <div className="border-t border-border my-2 pt-2">
                    <button
                      onClick={() => handleProfileMenuClick('/profile-management')}
                      className="flex items-center space-x-3 w-full px-4 py-3 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors min-h-[48px]"
                    >
                      <Icon name="User" size={20} />
                      <span>Profile Settings</span>
                    </button>

                    {userProfile?.role === 'admin' && (
                      <button
                        onClick={() => handleProfileMenuClick('/admin-portal')}
                        className="flex items-center space-x-3 w-full px-4 py-3 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors min-h-[48px]"
                      >
                        <Icon name="Shield" size={20} />
                        <span>Admin Portal</span>
                      </button>
                    )}

                    <button
                      onClick={() => handleProfileMenuClick('/help-support')}
                      className="flex items-center space-x-3 w-full px-4 py-3 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors min-h-[48px]"
                    >
                      <Icon name="HelpCircle" size={20} />
                      <span>Help & Support</span>
                    </button>

                    <button
                      onClick={handleSignOut}
                      className="flex items-center space-x-3 w-full px-4 py-3 rounded-lg text-sm text-destructive hover:bg-destructive/10 transition-colors min-h-[48px]"
                    >
                      <Icon name="LogOut" size={20} />
                      <span>Sign Out</span>
                    </button>
                  </div>
                </>
              ) : (
                <div className="px-4 py-3 flex flex-col space-y-2">
                  <Link
                    to="/login"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="w-full px-4 py-3 text-center text-sm font-medium text-foreground border border-border rounded-lg hover:bg-muted/50 transition-colors min-h-[48px] flex items-center justify-center"
                  >
                    Sign In
                  </Link>
                  <Link
                    to="/registration"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="w-full px-4 py-3 text-center text-sm font-medium bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors min-h-[48px] flex items-center justify-center"
                  >
                    Get Started
                  </Link>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;